// -----------------------------------------------------------
// FloraIQ - Tree Methodology Detail Drawer
// -----------------------------------------------------------

import { type FC } from "react";
import { treeMethodologyDetailExample } from "@/mocks/ladm";

interface TreeMethodologyDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const TreeMethodologyDetailDrawer: FC<TreeMethodologyDetailDrawerProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const data = treeMethodologyDetailExample;

  const qualityColor = data.qualidadeCadastro.score >= 90 ? "text-green-700 bg-green-50" : data.qualidadeCadastro.score >= 70 ? "text-orange-700 bg-orange-50" : "text-red-700 bg-red-50";

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose}></div>
      <div className="relative w-full max-w-lg bg-white shadow-xl overflow-y-auto slide-in-from-right">
        <div className="sticky top-0 bg-white border-b border-background-200/70 px-5 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="text-sm font-semibold text-foreground-900 font-heading">Detalhe Metodológico</h3>
            <p className="text-xs text-foreground-500 font-mono mt-0.5">{data.codigoCta} — {data.especie}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors">
            <i className="ri-close-line text-foreground-600"></i>
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Header info */}
          <div className="p-4 rounded-card bg-background-50/70 border border-background-200/50">
            <p className="text-xs text-foreground-500">Espécie</p>
            <p className="text-sm font-semibold text-foreground-900 font-heading">{data.especie}</p>
            <p className="text-xs text-foreground-400 italic">{data.nomeCientifico}</p>
            <p className="text-xs text-foreground-500 font-mono mt-2">Coordenada: {data.coordenada}</p>
          </div>

          {/* Qualidade */}
          <div className={`p-3 rounded-card ${qualityColor} flex items-center justify-between`}>
            <span className="text-xs font-medium">Qualidade do Cadastro</span>
            <div className="text-right">
              <span className="text-lg font-bold font-heading">{data.qualidadeCadastro.score}%</span>
              <p className="text-xs">{data.qualidadeCadastro.status}</p>
            </div>
          </div>

          {/* Unidades Espaciais */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-600 font-heading mb-2">Unidades Espaciais Vinculadas</h4>
            <div className="space-y-1.5">
              {data.unidadesEspaciais.map((u) => (
                <div key={u.nome} className="flex items-center gap-2 text-xs text-foreground-700">
                  <i className="ri-map-pin-line text-blue-500"></i>
                  <span className="text-foreground-400">{u.tipo}:</span>
                  <span className="font-medium">{u.nome}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Partes Envolvidas */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-600 font-heading mb-2">Partes Envolvidas</h4>
            <div className="space-y-2">
              {data.partesEnvolvidas.map((p) => (
                <div key={p.nome} className="flex items-center gap-2 bg-background-50/70 rounded-lg p-2 text-xs">
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                    <i className="ri-user-3-line text-blue-600 text-xs"></i>
                  </div>
                  <div>
                    <p className="font-medium text-foreground-900">{p.nome}</p>
                    <p className="text-foreground-400">{p.papel}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RRR */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-600 font-heading mb-2">RRR Vinculados</h4>
            <div className="space-y-2">
              {data.rrrVinculados.map((rrr, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-foreground-700">
                  <span className={`w-1.5 h-1.5 rounded-full ${rrr.tipo === "Responsabilidade" ? "bg-blue-500" : "bg-orange-500"}`}></span>
                  {rrr.descricao}
                </div>
              ))}
            </div>
          </div>

          {/* Fontes */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-600 font-heading mb-2">Fontes Documentais</h4>
            <div className="space-y-2">
              {data.fontesDocumentais.map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-foreground-700">
                  <i className="ri-file-text-line text-foreground-400"></i>
                  <span className="text-foreground-400">{f.tipo}:</span>
                  <span className="font-mono font-medium">{f.nome}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Nuvem de Pontos */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-600 font-heading mb-2">Nuvem de Pontos</h4>
            <div className="p-3 rounded-card bg-indigo-50/50 border border-indigo-100/60">
              <p className="text-xs font-medium text-indigo-800">{data.nuvemDePontos.nome}</p>
              <p className="text-xs text-indigo-600 mt-1">Status: {data.nuvemDePontos.status} · {data.nuvemDePontos.tiles}</p>
            </div>
          </div>

          {/* Eventos */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-600 font-heading mb-2">Eventos Históricos</h4>
            <div className="space-y-2">
              {data.eventos.map((e, i) => (
                <div key={i} className="flex items-center gap-3 text-xs">
                  <span className="text-foreground-400 font-mono whitespace-nowrap">{new Date(e.data + "T00:00:00").toLocaleDateString("pt-BR")}</span>
                  <span className="text-foreground-700">{e.evento}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-background-200/50">
            <button className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap">
              <i className="ri-file-text-line mr-1"></i>Ver FCA
            </button>
            <button className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap">
              <i className="ri-map-pin-line mr-1"></i>Abrir Mapa
            </button>
            <button className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap">
              <i className="ri-folder-open-line mr-1"></i>Ver Documentos
            </button>
            <button className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap">
              <i className="ri-history-line mr-1"></i>Ver Histórico
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TreeMethodologyDetailDrawer;